import React, { useEffect, useRef, useState } from 'react';

/**
 * Animated SVG line chart illustrating a rising trend (e.g. market volume).
 *
 * • Draws a gold gradient line from left to right on scroll-into-view.
 * • Fills a soft area beneath the line.
 * • Fades in data-point circles with a stagger.
 * • Sweeps a glowing pulse along the line once, then subtle idle pulse.
 *
 * Props:
 *   points   : Array<{ x: string|number, y: number }>  — data (required)
 *   yMin     : number  — axis lower bound (default: auto)
 *   yMax     : number  — axis upper bound (default: auto)
 *   yLabel   : string  — small tag under Y axis
 *   unit     : string  — unit suffix on data-point tooltips
 *   accent   : "gold" | "warm" — colour palette (default "gold")
 */
export default function GrowthChart({
  points,
  yMin,
  yMax,
  yLabel = '',
  unit = '',
  accent = 'gold',
  className = '',
}) {
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
          setVisible(true);
        }
      },
      { threshold: [0, 0.3, 0.6] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // --- geometry --------------------------------------------------------
  const W = 1500;
  const H = 300;
  const padL = 60;
  const padR = 30;
  const padT = 30;
  const padB = 42;

  const ys = points.map((p) => p.y);
  const lo = yMin ?? Math.floor(Math.min(...ys) * 0.98);
  const hi = yMax ?? Math.ceil(Math.max(...ys) * 1.02);
  const range = Math.max(1e-6, hi - lo);

  const xStep = (W - padL - padR) / Math.max(1, points.length - 1);
  const coords = points.map((p, i) => ({
    x: padL + i * xStep,
    y: padT + (H - padT - padB) * (1 - (p.y - lo) / range),
    raw: p,
  }));

  // Smooth cubic path through data points.
  const linePath = (() => {
    if (coords.length < 2) return '';
    const cmds = [`M ${coords[0].x} ${coords[0].y}`];
    for (let i = 1; i < coords.length; i += 1) {
      const p0 = coords[i - 1];
      const p1 = coords[i];
      const mid = (p0.x + p1.x) / 2;
      cmds.push(`C ${mid} ${p0.y}, ${mid} ${p1.y}, ${p1.x} ${p1.y}`);
    }
    return cmds.join(' ');
  })();

  const areaPath = coords.length >= 2
    ? `${linePath} L ${coords[coords.length - 1].x} ${H - padB} L ${coords[0].x} ${H - padB} Z`
    : '';

  // Y-axis grid values (4 lines including bounds).
  const yTicks = 4;
  const yValues = Array.from({ length: yTicks + 1 }, (_, i) =>
    lo + (range * i) / yTicks);

  const palette = accent === 'warm'
    ? { top: '#ffd6a1', mid: '#e88a2c', bot: '#8a3f0e' }
    : { top: '#f9ecb4', mid: '#d6b25a', bot: '#a37f2e' };

  const gradientId = `growth-line-${accent}`;
  const areaGradientId = `growth-area-${accent}`;

  return (
    <div
      ref={containerRef}
      className={`growth-chart ${visible ? 'is-visible' : ''} ${className}`}
      data-testid="growth-chart"
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={palette.top} />
            <stop offset="55%" stopColor={palette.mid} />
            <stop offset="100%" stopColor={palette.bot} />
          </linearGradient>
          <linearGradient id={areaGradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={palette.mid} stopOpacity="0.28" />
            <stop offset="100%" stopColor={palette.mid} stopOpacity="0" />
          </linearGradient>
          <filter id={`glow-${accent}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Y-axis grid + labels */}
        {yValues.map((v, i) => {
          const y = padT + (H - padT - padB) * (1 - i / yTicks);
          return (
            <g key={`grid-${i}`} className="growth-grid">
              <line x1={padL} x2={W - padR} y1={y} y2={y} />
              <text x={padL - 12} y={y + 4} textAnchor="end">
                {v.toFixed(0)}
              </text>
            </g>
          );
        })}
        {yLabel && (
          <text x={padL - 12} y={padT - 12} textAnchor="end" className="growth-axis-label">
            {yLabel}
          </text>
        )}

        {/* X-axis labels */}
        {coords.map((c, i) => (
          <text
            key={`x-${i}`}
            x={c.x}
            y={H - padB + 24}
            textAnchor="middle"
            className="growth-x-label"
          >
            {points[i].x}
          </text>
        ))}

        {/* Area under the curve */}
        {areaPath && (
          <path
            d={areaPath}
            fill={`url(#${areaGradientId})`}
            className="growth-area"
          />
        )}

        {/* Rising line */}
        {linePath && (
          <path
            d={linePath}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={`url(#glow-${accent})`}
            className="growth-line"
          />
        )}

        {/* Data-point dots with staggered fade-in + labels */}
        {coords.map((c, i) => (
          <g
            key={`dot-${i}`}
            className="growth-dot"
            style={{ transitionDelay: `${800 + i * 640}ms`, animationDelay: `${800 + i * 640}ms` }}
          >
            <circle
              cx={c.x}
              cy={c.y}
              r="8"
              fill={palette.mid}
              opacity="0.15"
            />
            <circle
              cx={c.x}
              cy={c.y}
              r="4"
              fill={palette.top}
              stroke={palette.bot}
              strokeWidth="1"
            />
            <text
              x={c.x}
              y={c.y - 14}
              textAnchor="middle"
              className="growth-value"
            >
              {c.raw.y}{unit}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
